import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetSavedAmazonCredentialsList } from "@via/pages/data/data-sources/data-source-credentials.api.tsx";
import { useForm } from "react-hook-form";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import {
  cloudConnectionOptions,
  datasourceOptions,
} from "@via/utils/form-validators.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { useAmazonS3FolderList } from "@via/pages/data/data-sources/data-source.api.tsx";
import { useAmazonS3Connection } from "@via/hooks/useAmazonS3Connection.tsx";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import CredentialKeyForm from "@via/pages/account/profile/credentials/credential-key-form/credential-key-form.tsx";
import Drawer from "@via/components/drawer.tsx";
import { UpsertCredentialsResponse } from "common";
import {
  BUILT_IN_ACCESS_OPTION_ID,
  BUILT_IN_ACCESS_OPTION_LABEL,
} from "@via/pages/data/constants.ts";

interface CloudConnectionCredentials {
  savedCredentialId: number;
  bucketName: string;
}

const AmazonS3Connector: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { setConnectionCredentials } = useAmazonS3Connection();

  const { mutateAsync: getFolderList, isPending: isGetFolderListLoading } =
    useAmazonS3FolderList();

  const { isLoading: isSavedCredentialsLoading, data: savedCredentials = [] } =
    useGetSavedAmazonCredentialsList();
  const credentialList = [
    { name: "New credentials", id: 0 },
    { name: BUILT_IN_ACCESS_OPTION_LABEL, id: BUILT_IN_ACCESS_OPTION_ID },
    ...savedCredentials,
  ];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CloudConnectionCredentials>({
    mode: "onChange",
    defaultValues: {
      bucketName: "s3://",
      savedCredentialId: BUILT_IN_ACCESS_OPTION_ID,
    },
  });
  const savedCredentialIdValue = watch("savedCredentialId");

  useEffect(() => {
    if (savedCredentialIdValue === 0) {
      setIsDrawerOpen(true);
    }
  }, [savedCredentialIdValue]);

  const handleCloudConnectionFormSubmit = async (
    payload: CloudConnectionCredentials
  ) => {
    const requestParams = Object.assign({}, payload);
    await getFolderList(requestParams);
    setConnectionCredentials(requestParams);
    const datasetId = searchParams.get("datasetId");
    const url = datasetId
      ? `${ROUTES.DATA_SOURCE_PROVIDER_AMAZON}?datasetId=${datasetId}`
      : `${ROUTES.DATA_SOURCE_PROVIDER_AMAZON}`;
    navigate(url);
  };

  const handleCancel = (): void => {
    navigate(ROUTES.DATA);
  };

  const handleCredentialsCreate = (
    credentialsResponse: UpsertCredentialsResponse
  ) => {
    if (credentialsResponse?.amazonCredentialsId) {
      setValue("savedCredentialId", credentialsResponse.amazonCredentialsId);
    }
  };

  return (
    <div>
      {isSavedCredentialsLoading ? (
        <div className="flex flex-col gap-2 py-6">
          <SkeletonLoader count={2} />
        </div>
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit(handleCloudConnectionFormSubmit)(e);
          }}>
          <div className="max-w-[850px] mx-auto space-y-6 pb-5 pt-3">
            <div className="max-w-[850px] space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Choose Credentials&nbsp;
                  <span className="text-red-400">*</span>
                </label>
                <div className="mt-2" data-testid="credentialsSelector">
                  <SelectComponent
                    control={control}
                    name="savedCredentialId"
                    rules={cloudConnectionOptions.savedCredentialId}
                    formatOptionLabel={option => {
                      return option.value === 0 ? (
                        <div className="flex flex-row items-center font-semibold">
                          <PlusCircleIcon className="h-5 w-5 mr-2" />{" "}
                          {option.label}
                        </div>
                      ) : (
                        option.label
                      );
                    }}
                    options={
                      credentialList?.map(({ name, id }) => ({
                        label: name,
                        value: id,
                      })) ?? []
                    }
                    isLoading={isSavedCredentialsLoading}
                    placeholder="Choose Credentials"
                  />
                  {errors.savedCredentialId && (
                    <span className="text-xs text-rose-500">
                      {errors.savedCredentialId.message}
                    </span>
                  )}
                </div>
              </div>

              <FormInputComponent
                control={control}
                type={"text"}
                name="bucketName"
                label="Data Source Path"
                placeholder="s3://"
                tooltipText={"The existing cloud data source path to access"}
                showTooltipIcon
                validations={datasourceOptions.path}
                showRequiredField
              />
            </div>

            <div className="flex flex-shrink-0 justify-end py-4">
              <button
                type="button"
                data-testid="cloudConnectorCancelButton"
                onClick={handleCancel}
                disabled={isGetFolderListLoading}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Cancel
              </button>
              <button
                data-testid="cloudConnectorSubmitButton"
                type="submit"
                disabled={isGetFolderListLoading}
                className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <Spinner loading={isGetFolderListLoading}>Connect</Spinner>
              </button>
            </div>
          </div>
        </form>
      )}
      <Drawer id="credentials" open={isDrawerOpen} setOpen={setIsDrawerOpen}>
        <CredentialKeyForm
          setOpen={setIsDrawerOpen}
          givenCredentialType={"amazon"}
          onSuccess={handleCredentialsCreate}
        />
      </Drawer>
    </div>
  );
};

export default AmazonS3Connector;
